import { useMemo, useState } from 'react';
import Graphic from '@arcgis/core/Graphic';
import Point from '@arcgis/core/geometry/Point';
import { StylishNewCheckbox } from 'components/DesignSystems/New/StylishNewCheckbox';
import { IconButton } from 'components/DesignSystems/buttons/IconButton';
import { formatDateTime } from 'components/IAP/Resources/utils/dateUtils';
import { useListTakEntitiesQuery } from 'hooks/takEntity/useListTakEntitiesQuery';
import type { TakEntity } from 'hooks/takEntity/types';
import { FeatureItem } from './FeatureItem';
import { Layer, FeatureDetailRows, PropertyRow } from './Layer';
import { useUscgMapContext } from './UscgMapContext';

function entityTitle(entity: TakEntity): string {
  return entity.callsign?.trim() || entity.uid;
}

function entityPointGraphic(entity: TakEntity): Graphic {
  return new Graphic({
    geometry: new Point({ longitude: entity.lon, latitude: entity.lat }),
    attributes: {
      takUid: entity.uid,
      name: entityTitle(entity),
      category: entity.category,
      cotType: entity.cotType,
    },
  });
}

export function TakLayerItem() {
  const { mapView, isTakVisible, toggleTakVisibility, focusFeatureOnMap } =
    useUscgMapContext();

  const { data, isLoading } = useListTakEntitiesQuery({
    enabled: true,
  });
  const entities = data?.data ?? [];
  const [searchTerm, setSearchTerm] = useState('');

  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return entities;
    return entities.filter((entity) => {
      const haystack = [
        entity.callsign,
        entity.uid,
        entity.category,
        entity.cotType,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [entities, searchTerm]);

  const zoomToLayer = async () => {
    if (!mapView || entities.length === 0) return;
    if (!isTakVisible) toggleTakVisibility();
    const graphics = entities.map(entityPointGraphic);
    await mapView.goTo(graphics, { duration: 500 });
  };

  const zoomToEntity = async (entity: TakEntity) => {
    if (!isTakVisible) toggleTakVisibility();
    await focusFeatureOnMap({ graphic: entityPointGraphic(entity) });
  };

  return (
    <Layer.Item
      eventKey="tak-layer"
      header={
        <div className="d-flex justify-content-between align-items-center w-100">
          <div className="d-flex align-items-center gap-2">
            <StylishNewCheckbox
              className="mt-0"
              checked={isTakVisible}
              onClick={(e) => {
                e.stopPropagation();
                toggleTakVisibility();
              }}
            />
            <span className="fw-semibold">TAK (Team Awareness Kit)</span>
          </div>
          <IconButton
            iconProps={{ iconName: 'map' }}
            onClick={(e) => {
              e.stopPropagation();
              void zoomToLayer();
            }}
            aria-label="Zoom to TAK entities"
            title="Zoom to TAK entities"
            disabled={entities.length === 0}
          />
        </div>
      }
    >
      <Layer.Content>
        <PropertyRow label="Entities" value={String(entities.length)} />
        <Layer.FeaturesList
          isLoading={isLoading}
          searchTerm={searchTerm}
          onChangeSearchTerm={setSearchTerm}
          showSearch
          showAddObjectButton={false}
        >
          {filtered.length === 0 ? (
            <p className="small text-muted mb-0 p-2 pt-0">
              {entities.length === 0
                ? 'No TAK entities available.'
                : 'No entities match this search.'}
            </p>
          ) : (
            <ul className="list-unstyled d-grid mb-0 gap-2">
              {filtered.map((entity) => (
                <FeatureItem
                  key={entity.uid}
                  title={entityTitle(entity)}
                  externalFeature={{ featureId: entity.uid }}
                  onZoom={() => zoomToEntity(entity)}
                >
                  <FeatureDetailRows
                    lastUpdated={formatDateTime(entity.updatedAt)}
                    point={{
                      latitude: entity.lat,
                      longitude: entity.lon,
                    }}
                  />
                  <PropertyRow label="Category" value={entity.category} />
                  <PropertyRow label="CoT type" value={entity.cotType} />
                  <PropertyRow label="UID" value={entity.uid} />
                </FeatureItem>
              ))}
            </ul>
          )}
        </Layer.FeaturesList>
      </Layer.Content>
    </Layer.Item>
  );
}
